import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

interface EmptyStateProps {
  variant: "cart" | "favorites";
  title: string;
  description: string;
}

function CartIllustration() {
  return (
    <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
      <ellipse cx="80" cy="104" rx="52" ry="6" fill="currentColor" opacity="0.08" />
      <path
        d="M42 44h76l-7 54a8 8 0 0 1-8 7H57a8 8 0 0 1-8-7L42 44Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.55"
      />
      <path
        d="M64 44V34a16 16 0 0 1 32 0v10"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.9"
      />
      <circle cx="80" cy="72" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
    </svg>
  );
}

function HeartIllustration() {
  return (
    <svg viewBox="0 0 160 120" className="w-full h-full" aria-hidden="true">
      <ellipse cx="80" cy="106" rx="46" ry="6" fill="currentColor" opacity="0.08" />
      <path
        d="M80 96S44 74 44 52a19 19 0 0 1 36-8 19 19 0 0 1 36 8c0 22-36 44-36 44Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        opacity="0.75"
      />
      <path d="M62 44a10 10 0 0 1 8-6" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.4" />
    </svg>
  );
}

export function EmptyState({ variant, title, description }: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="py-16 md:py-24 flex flex-col items-center text-center"
    >
      <div className="w-40 h-32 md:w-52 md:h-40 text-secondary mb-8">
        {variant === "cart" ? <CartIllustration /> : <HeartIllustration />}
      </div>
      <h2 className="text-headline-md font-headline-md text-primary mb-3">{title}</h2>
      <p className="text-body-md font-body-md text-on-surface-variant max-w-sm mb-8">
        {description}
      </p>
      <Link
        to="/shop"
        className="inline-flex items-center justify-center px-10 py-4 bg-primary text-on-primary rounded-lg text-label-md font-label-md uppercase tracking-widest hover:bg-primary/90 transition-colors"
      >
        {t("common.shopNow")}
      </Link>
    </motion.div>
  );
}
